import {
  useCodeiumCommand,
  useCodeiumFlag,
} from "commands/definitions/settings/useCodeiumCommand";
import { useColorSchemaCommand } from "commands/definitions/settings/useColorSchemaCommand";
import { useOpenEditorSettingsCommand } from "commands/definitions/vscode/useOpenEditorSettingsCommand";
import { Box, Button, Stack, Switch, Text, Tooltip, styled } from "prism-react";
import { useState, useEffect } from "react";

import { getShortcut } from "commands/utils";
import { useAnalytics } from "hooks/useAnalytics";
import { useGlobalPersistedState } from "hooks/usePersistedState";

import { SettingItem } from "./common";

export const EditorPreference: React.FC<{ onClose: () => void }> = ({
  onClose,
}) => {
  const openEditorSettings = useOpenEditorSettingsCommand("ui");
  const changeTheme = useColorSchemaCommand();
  const [vimEnabled, setVimEnabled] = useGlobalPersistedState(
    "VSCODE_VIM_EXTENSION",
    false,
  );
  const [usePointers, setUsePointers] = useGlobalPersistedState(
    "CURSOR_POINTER",
    true,
  );

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--csb-cursor",
      usePointers ? "pointer" : "default",
    );
  }, [usePointers]);

  const [showVimAlert, setShowVimAlert] = useState(false);

  return (
    <Stack gap={5}>
      <CodeiumSettings />
      <SettingItem
        description="Use the command palette to control VIM"
        message={
          showVimAlert && (
            <Stack horizontal>
              <Text color="informative-warning-base" css={{ flex: 1 }}>
                You need to refresh the browser for this to take effect, do you
                want to do that now?
              </Text>
              <Button
                onClick={() => window.location.reload()}
                variant="secondary"
              >
                Reload
              </Button>
            </Stack>
          )
        }
        title="Enable VIM extension"
      >
        <Switch
          aria-label="Enable VIM extension"
          defaultChecked={vimEnabled}
          onCheckedChange={(value) => {
            setVimEnabled(value);
            setShowVimAlert(true);
          }}
        />
      </SettingItem>

      <SettingItem
        description="All interactive elements show a pointer cursor on hover"
        title="Use pointer cursors"
      >
        <Tooltip
          content={
            usePointers ? "Shows pointer cursors" : "Shows default cursors"
          }
        >
          <Switch
            defaultChecked={usePointers}
            onCheckedChange={(value) => {
              setUsePointers(value);
            }}
          />
        </Tooltip>
      </SettingItem>

      <Stack
        css={{ padding: "$2 0", color: "$informative-info-base" }}
        gap={1}
        horizontal
      >
        <Text>
          Editor settings like font/tab size or themes have moved to{" "}
          <UnstyledButton
            as="button"
            onClick={() => {
              onClose();
              openEditorSettings.perform();
            }}
          >
            Editor {">"} User Preferences
          </UnstyledButton>{" "}
          and{" "}
          <UnstyledButton
            as="button"
            onClick={() => {
              onClose();
              changeTheme.perform();
            }}
          >
            Editor {">"} Color Theme
          </UnstyledButton>
          .
        </Text>
      </Stack>
    </Stack>
  );
};

export const CodeiumSettings = ({
  showShortcut = true,
}: {
  showShortcut?: boolean;
}) => {
  const codeiumCommand = useCodeiumCommand();
  const [codeCompletion, setCodeCompletion] = useCodeiumFlag();

  const track = useAnalytics();

  return (
    <SettingItem
      description={
        <>
          {showShortcut ? (
            <>
              Supercharge your development by Codeium's code completion tool,{" "}
              <Box
                className="monaco-keybinding"
                css={{ display: "inline-flex !important" }}
              >
                {getShortcut(codeiumCommand.binding || "").map((key) => (
                  <TooltipShortcut key={key}>{key}</TooltipShortcut>
                ))}
              </Box>{" "}
              to toggle this feature.
            </>
          ) : (
            "Supercharge your development by Codeium's code completion tool."
          )}{" "}
          <a
            href="https://codesandbox.io/docs/learn/ai/codeium"
            rel="noreferrer"
            target="_blank"
          >
            Learn more.
          </a>
        </>
      }
      title={"Code completion by Codeium: {…}"}
    >
      <Switch
        defaultChecked={codeCompletion}
        onCheckedChange={(value) => {
          if (value === true) {
            track("codeium_enable", { location: "settings" });
          } else {
            track("codeium_disable", { location: "settings" });
          }

          setCodeCompletion(value);
        }}
      />
    </SettingItem>
  );
};

const UnstyledButton = styled(Button, {
  padding: 0,
  color: "inherit",
  fontSize: "inherit",
  fontWeight: "inherit",
  border: 0,
  textDecoration: "underline",
});

const TooltipShortcut: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <Box
      className="monaco-keybinding-key"
      css={{
        backgroundColor: "var(--vscode-keybindingLabel-background)",

        borderBottomColor: "var(--vscode-keybindingLabel-bottomBorder)",
        boxShadow: "inset 0 -1px 0 var(--vscode-widget-shadow)",
      }}
    >
      {children}
    </Box>
  );
};
